import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import contactsApi from '../api/contacts';
import { getProperties } from '../api/properties';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  relationship: '',
  phone: '',
  company: '',
  property: '',
  notes: '',
};

const relationships = [
  { value: '', label: 'Select relationship' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Agent', label: 'Agent' },
  { value: 'Tenant', label: 'Tenant' },
  { value: 'Accountant', label: 'Accountant' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Other', label: 'Other' },
];

function AddContact({ onSave, properties: initialProperties = [] }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState(initialProperties);
  const [loadingProperties, setLoadingProperties] = useState(initialProperties.length === 0);
  const { run: runSubmission, isPending } = useSubmissionLock();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;

    getProperties()
      .then((res) => {
        if (!alive) return;
        setProperties(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to load properties:', err);
        if (alive) setProperties([]);
      })
      .finally(() => {
        if (alive) setLoadingProperties(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Update form state for input/select/textarea changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Check if required fields are filled
  const isFormValid =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    form.relationship.trim() &&
    form.phone.trim();

  // Submit form data to API and navigate on success
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || isPending('contact:create:standalone')) return;

    await runSubmission('contact:create:standalone', async () => {
      setSaving(true);

      try {
        const newContact = await contactsApi.createContact({
          ...form,
          name: `${form.firstName} ${form.lastName}`.trim(),
          propertyId: form.property || null,
        });

        console.log('Created contact:', newContact);

        if (onSave) onSave(newContact);

        const propertyQuery = form.property
          ? `?propertyId=${encodeURIComponent(form.property)}`
          : '';
        const shouldOpenOnboarding =
          String(form.relationship || '').toLowerCase() === 'tenant';

        setForm(initialState);
        navigate(shouldOpenOnboarding ? `/contact/${newContact.id}${propertyQuery}` : '/contacts');
      } catch (err) {
        console.error('Failed to create contact:', err);
        alert('Error creating contact');
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto mt-10 bg-white/10 rounded-xl border border-white/10 p-8 backdrop-blur-md text-white">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-6 text-white hover:text-[#AF9B8A] transition"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>

      <h2 className="text-2xl font-bold mb-6">Add Contact</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Fields */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="firstName" className="block mb-1 font-semibold">
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={form.firstName}
              onChange={handleChange}
              required
              placeholder="First name"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            />
          </div>

          <div className="flex-1">
            <label htmlFor="lastName" className="block mb-1 font-semibold">
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={form.lastName}
              onChange={handleChange}
              required
              placeholder="Last name"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            />
          </div>
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block mb-1 font-semibold">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder="Email address"
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
          />
        </div>

        {/* Relationship and Phone */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <label htmlFor="relationship" className="block mb-1 font-semibold">
              Relationship
            </label>
            <select
              id="relationship"
              name="relationship"
              value={form.relationship}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-gray-400 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              style={{ cursor: 'pointer' }}
            >
              {relationships.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-10 pointer-events-none w-4 h-4 text-white/70" />
          </div>

          <div className="flex-1">
            <label htmlFor="phone" className="block mb-1 font-semibold">
              Phone
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              required
              placeholder="Phone number"
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
            />
          </div>
        </div>

        {/* Company */}
        <div>
          <label htmlFor="company" className="block mb-1 font-semibold">
            Company
          </label>
          <input
            id="company"
            name="company"
            type="text"
            value={form.company}
            onChange={handleChange}
            placeholder="Company (optional)"
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
          />
        </div>

        {/* Property */}
        <div className="relative">
          <label htmlFor="property" className="block mb-1 font-semibold">
            Property
          </label>
          <select
            id="property"
            name="property"
            value={form.property}
            onChange={handleChange}
            disabled={loadingProperties}
            className="appearance-none w-full px-4 py-2 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            style={{ cursor: 'pointer' }}
          >
            <option value="">
              {loadingProperties ? 'Loading properties...' : 'Select property (optional)'}
            </option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-4 top-11 pointer-events-none w-4 h-4 text-white/70" />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block mb-1 font-semibold">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
            placeholder="Additional notes"
            className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={saving || !isFormValid}
          className={`w-full py-3 rounded-lg text-white font-semibold transition ${
            saving || !isFormValid
              ? 'bg-[#AF9B8A]/50 cursor-not-allowed'
              : 'bg-[#AF9B8A] hover:bg-[#AF9B8A]/80'
          }`}
          aria-live="polite"
        >
          {saving ? 'Adding contact...' : 'Add Contact'}
        </button>
      </form>
    </div>
  );
}

export default AddContact;
